import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { getWeaponMasteryEntitlements, getSelectionsForSource } from '../utils/weaponMastery';
import { weaponMasteryChoiceKey, HUNTERS_PREY_CHOICE_KEY, DEFENSIVE_TACTICS_CHOICE_KEY } from '../utils/featureChoices';
import { getHuntersPrey, canChangeHuntersPrey, HUNTERS_PREY_OPTIONS, getDefensiveTactics, canChangeDefensiveTactics, DEFENSIVE_TACTICS_OPTIONS } from '../../../shared/rules/subclasses/ranger/huntersPrey.mjs';
import { REST_SPELL_CHANGES, PROMPTED_SPELL_SWAP_CLASSES } from '../../../shared/rules/restSpellChanges.mjs';
import { getSpellSlotProfile } from '../../../shared/rules/spellSlots.mjs';
import { getClassLevel } from '../../../shared/rules/classIdentity.mjs';
import { HUNTERS_MARK_SPELL_ID } from '../../../shared/rules/ranger.mjs';
import { DIVINE_SMITE_SPELL_ID, FIND_STEED_SPELL_ID } from '../../../shared/rules/paladin.mjs';
import { getDevotionOathSpells } from '../../../shared/rules/subclasses/paladin/devotion.mjs';
import { getLifeDomainSpells } from '../../../shared/rules/subclasses/cleric/life.mjs';
import { getSpellbook, hasWizardFeature } from '../../../shared/rules/wizard.mjs';
import { LANDS, getLand, hasLandFeature } from '../../../shared/rules/subclasses/druid/land.mjs';
import { getKnownForms } from '../../../shared/rules/beastForm';
import spellCatalog from '../../../data/spells.json';

// Spells a class feature keeps prepared (Favored Enemy, Paladin's Smite, Faithful Steed) are never swap options.
const ALWAYS_PREPARED = Object.freeze({ ranger: [HUNTERS_MARK_SPELL_ID], paladin: [DIVINE_SMITE_SPELL_ID, FIND_STEED_SPELL_ID] });

const spellIdFromName = (name) => String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const catalogById = new Map(spellCatalog.map((spell) => [spellIdFromName(spell.name), spell]));
const spellName = (spellId) => catalogById.get(spellId)?.name || spellId;
const classSpells = (form, classId) => (Array.isArray(form?.spells) ? form.spells : [])
  .filter((spell) => spell?.source?.sourceType === 'class' && spell.source.sourceId === classId && spell.spellId);

/**
 * Prepared-spell swap after a rest (2024 "Changing Your Prepared Spells"): replace one prepared spell of
 * the class with another spell from its list of a level the character has spell slots for.
 */
export const preparedSpellSwap = (form, classId, catalog = spellCatalog) => {
  const prepared = classSpells(form, classId)
    .map((spell) => ({ spellId: spell.spellId, name: spell.name || spellName(spell.spellId), level: Number(spell.level ?? catalogById.get(spell.spellId)?.level) }))
    .filter((spell) => spell.level > 0);
  const maximums = getSpellSlotProfile(form).spellcasting.maximumByLevel;
  const highest = Math.max(0, ...Object.entries(maximums).filter(([, count]) => count > 0).map(([level]) => Number(level)));
  // Always-prepared spells (class features, Oath and domain spells) are never offered as a swap.
  const taken = new Set([...prepared.map((spell) => spell.spellId), ...(ALWAYS_PREPARED[classId] || []), ...(classId === 'paladin' ? getDevotionOathSpells(form) : []), ...(classId === 'cleric' ? getLifeDomainSpells(form) : [])]);
  const options = catalog
    .filter((spell) => (spell.classes || []).map((entry) => String(entry).toLowerCase()).includes(classId))
    .map((spell) => ({ spellId: spellIdFromName(spell.name), name: spell.name, level: Number(spell.level) }))
    .filter((spell) => spell.level >= 1 && spell.level <= highest && !taken.has(spell.spellId))
    .sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));
  return { prepared, options };
};

const titleCase = (value) => String(value || '').replace(/(^|[\s-])(\w)/g, (match, gap, letter) => `${gap}${letter.toUpperCase()}`);

/**
 * Feature choices a finished rest lets the character change: Weapon Mastery after a Long Rest
 * (while replacements remain), one prepared spell after a Long Rest (Ranger today), and
 * Hunter's Prey after a Short or Long Rest. A Druid can change the Circle of the Land's land and
 * replace one Wild Shape form after a Long Rest; the server checks both.
 */
export const restChangeableChoices = (form, restType) => {
  const choices = [];
  if (restType === 'long') {
    getWeaponMasteryEntitlements(form).forEach(({ sourceId, count }) => {
      const replacements = Math.max(0, Number(form?.weaponMasteries?.sources?.[sourceId]?.replacementsAvailable) || 0);
      const selections = getSelectionsForSource(form, sourceId);
      if (replacements < 1 || !selections.length) return;
      choices.push({ key: weaponMasteryChoiceKey(sourceId), kind: 'weapon-mastery', sourceId, count, replacements, current: selections,
        label: `Weapon Mastery (${titleCase(sourceId)})` });
    });
  }
  PROMPTED_SPELL_SWAP_CLASSES.forEach((classId) => {
    if (REST_SPELL_CHANGES[classId]?.rest !== restType || getClassLevel(form, classId) < 1) return;
    const { prepared, options } = preparedSpellSwap(form, classId);
    if (!prepared.length || !options.length) return;
    choices.push({ key: `prepared-spell:${classId}`, kind: 'prepared-spell', classId, prepared, options,
      current: { remove: '', add: '' }, label: `Prepared spell (${titleCase(classId)})` });
  });
  // Memorize Spell (Wizard 5): a Short Rest replaces one prepared spell with another from the spellbook.
  const book = getSpellbook(form);
  if (restType === 'short' && book && hasWizardFeature(form, 'memorizeSpell')) {
    const { prepared, options } = preparedSpellSwap(form, 'wizard', spellCatalog.filter((spell) => book.includes(spellIdFromName(spell.name))));
    if (prepared.length && options.length) choices.push({ key: 'prepared-spell:wizard', kind: 'prepared-spell', classId: 'wizard', prepared, options, current: { remove: '', add: '' }, label: 'Memorize Spell' });
  }
  if (restType === 'long' && hasLandFeature(form, 'circleSpells')) {
    choices.push({ key: 'druid-land', kind: 'druid-land', current: getLand(form)?.id || '', label: 'Land' });
  }
  if (restType === 'long' && getKnownForms(form).length) {
    choices.push({ key: 'known-form', kind: 'known-form', known: getKnownForms(form), current: { remove: '', add: '' }, label: 'Wild Shape form' });
  }
  const prey = getHuntersPrey(form);
  if (['long', 'short'].includes(restType) && prey && canChangeHuntersPrey(form)) {
    choices.push({ key: HUNTERS_PREY_CHOICE_KEY, kind: 'hunters-prey', current: prey.id, label: "Hunter's Prey" });
  }
  const tactics = getDefensiveTactics(form);
  if (['long', 'short'].includes(restType) && tactics && canChangeDefensiveTactics(form)) {
    choices.push({ key: DEFENSIVE_TACTICS_CHOICE_KEY, kind: 'defensive-tactics', current: tactics.id, label: 'Defensive Tactics' });
  }
  return choices;
};

/** Weapons in `draft` that replace the current selections (each counts against the replacements). */
export const masteryReplacements = (current, draft) => draft.filter((key) => key && !current.includes(key)).length;

const sameDraft = (choice, draft) => JSON.stringify(draft) === JSON.stringify(choice.current);

/**
 * After a Short or Long Rest, walk through each feature choice the rest unlocked (Continue / Back),
 * then review and save them together. Closing keeps the current choices. It waits while the
 * short-rest Hit Dice choice is open.
 */
export default function RestChoicesPrompt({ form, characterId, longRestCount, shortRestCount, blocked = false, onCharacterChange }) {
  const counts = useRef({ long: longRestCount, short: shortRestCount });
  const [session, setSession] = useState(null); // { restType, choices, drafts, step }
  const [weapons, setWeapons] = useState({});
  const [beasts, setBeasts] = useState(null);
  const [saving, setSaving] = useState(false), [error, setError] = useState('');

  useEffect(() => {
    const previous = counts.current;
    counts.current = { long: longRestCount, short: shortRestCount };
    const restType = longRestCount > previous.long ? 'long' : shortRestCount > previous.short ? 'short' : null;
    if (!restType) return;
    const choices = restChangeableChoices(form, restType);
    setError('');
    setSession(choices.length ? { restType, choices, step: 0, drafts: Object.fromEntries(choices.map((choice) => [choice.key,
      Array.isArray(choice.current) ? [...choice.current] : choice.current && typeof choice.current === 'object' ? { ...choice.current } : choice.current])) } : null);
    // Start a session only when a rest is first seen; later sheet updates must not reset the drafts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longRestCount, shortRestCount]);

  const needsWeapons = Boolean(session?.choices.some((choice) => choice.kind === 'weapon-mastery'));
  useEffect(() => {
    if (!needsWeapons) return undefined;
    let cancelled = false;
    apiFetch('/weapons').then((response) => (response?.ok ? response.json() : {})).then((data) => { if (!cancelled) setWeapons(data || {}); })
      .catch(() => { /* The selects still show the current weapons. */ });
    return () => { cancelled = true; };
  }, [needsWeapons]);

  const needsBeasts = Boolean(session?.choices.some((choice) => choice.kind === 'known-form'));
  useEffect(() => {
    if (!needsBeasts) return undefined;
    let cancelled = false;
    apiFetch(`/characters/${encodeURIComponent(form?._id || characterId)}/druid/beast-forms`, { cache: 'no-store' })
      .then((response) => (response?.ok ? response.json() : null)).then((data) => { if (!cancelled) setBeasts(data?.forms || []); })
      .catch(() => { if (!cancelled) setBeasts([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsBeasts]);

  if (!session || blocked) return null;
  const { choices, drafts, step, restType } = session;
  const reviewing = step >= choices.length;
  const choice = reviewing ? null : choices[step];
  const setDraft = (key, value) => setSession((previous) => ({ ...previous, drafts: { ...previous.drafts, [key]: value } }));
  const go = (next) => { setError(''); setSession((previous) => ({ ...previous, step: next })); };
  const close = () => { if (!saving) setSession(null); };

  const overLimit = choice?.kind === 'weapon-mastery' && masteryReplacements(choice.current, drafts[choice.key]) > choice.replacements;
  const duplicate = choice?.kind === 'weapon-mastery' && new Set(drafts[choice.key].filter(Boolean)).size !== drafts[choice.key].filter(Boolean).length;
  const incomplete = (choice?.kind === 'weapon-mastery' && drafts[choice.key].some((key) => !key))
    || (['prepared-spell', 'known-form'].includes(choice?.kind) && Boolean(drafts[choice.key].remove) !== Boolean(drafts[choice.key].add));

  const save = async () => {
    if (saving) return;
    setSaving(true); setError('');
    const id = encodeURIComponent(form?._id || characterId);
    try {
      for (const entry of choices.filter((item) => !sameDraft(item, drafts[item.key]))) {
        if (entry.kind === 'weapon-mastery') {
          const response = await apiFetch(`/characters/${id}/weapon-masteries/${entry.sourceId}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selections: drafts[entry.key] }) });
          const result = await response.json();
          if (!response.ok) throw new Error(`${entry.label}: ${result.message || 'could not be saved.'}`);
          const selections = drafts[entry.key];
          onCharacterChange?.((previous) => {
            const updated = { ...previous, weaponMasteries: result };
            // Keep the deprecated Barbarian mirror during the compatibility window (as Character Features does).
            return entry.sourceId === 'barbarian' ? { ...updated, classState: { ...previous.classState, barbarian: { ...previous.classState?.barbarian,
              weaponMasteries: { ...previous.classState?.barbarian?.weaponMasteries, selections } } } } : updated;
          });
        } else if (entry.kind === 'prepared-spell') {
          const { remove, add } = drafts[entry.key];
          const source = { sourceType: 'class', sourceId: entry.classId };
          const spells = [...classSpells(form, entry.classId).filter((spell) => spell.spellId !== remove).map((spell) => ({ spellId: spell.spellId, source })), { spellId: add, source }];
          const response = await apiFetch(`/characters/${id}/spell-sources/class/${entry.classId}/spells`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ spells }) });
          const result = await response.json();
          if (!response.ok) throw new Error(`${entry.label}: ${result.message || 'could not be saved.'}`);
          if (Array.isArray(result.spells)) onCharacterChange?.((previous) => ({ ...previous, spells: result.spells }));
        } else if (entry.kind === 'druid-land') {
          const response = await apiFetch(`/characters/${id}/classes/druid/land`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ landId: drafts[entry.key] }) });
          const result = await response.json();
          if (!response.ok) throw new Error(`${entry.label}: ${result.message || 'could not be saved.'}`);
          onCharacterChange?.((previous) => ({ ...previous, classState: { ...previous.classState, druid: { ...previous.classState?.druid, land: result.land } } }));
        } else if (entry.kind === 'known-form') {
          const { remove, add } = drafts[entry.key];
          const response = await apiFetch(`/characters/${id}/druid/known-forms`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ forms: entry.known.map((index) => (index === remove ? add : index)) }) });
          const result = await response.json();
          if (!response.ok) throw new Error(`${entry.label}: ${result.message || 'could not be saved.'}`);
          onCharacterChange?.((previous) => ({ ...previous, classState: { ...previous.classState, druid: { ...previous.classState?.druid, knownForms: result.knownForms } } }));
        } else if (entry.kind === 'defensive-tactics') {
          const response = await apiFetch(`/characters/${id}/classes/ranger/defensive-tactics`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tacticId: drafts[entry.key] }) });
          const result = await response.json();
          if (!response.ok) throw new Error(`${entry.label}: ${result.message || 'could not be saved.'}`);
          onCharacterChange?.((previous) => ({ ...previous, classState: { ...previous.classState, ranger: { ...previous.classState?.ranger, defensiveTactics: result.defensiveTactics } } }));
        } else {
          const response = await apiFetch(`/characters/${id}/classes/ranger/hunters-prey`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ preyId: drafts[entry.key] }) });
          const result = await response.json();
          if (!response.ok) throw new Error(`${entry.label}: ${result.message || 'could not be saved.'}`);
          onCharacterChange?.((previous) => ({ ...previous, classState: { ...previous.classState, ranger: { ...previous.classState?.ranger, huntersPrey: result.huntersPrey } } }));
        }
      }
      setSession(null);
    } catch (failure) { setError(failure.message); }
    finally { setSaving(false); }
  };

  const weaponName = (key) => weapons?.[key]?.name || titleCase(String(key).replace(/-/g, ' '));
  const preyName = (id) => HUNTERS_PREY_OPTIONS.find((option) => option.id === id)?.name || id;
  const beastName = (index) => beasts?.find((beast) => beast.index === index)?.name || titleCase(String(index).replace(/-/g, ' '));
  const describe = (item, value) => (item.kind === 'weapon-mastery' ? value.map(weaponName).join(', ')
    : item.kind === 'druid-land' ? LANDS[value]?.name || 'None' : preyName(value));
  const reviewLine = (item) => {
    const draft = drafts[item.key];
    if (item.kind === 'prepared-spell') return draft.remove ? `${spellName(draft.remove)} → ${spellName(draft.add)}` : 'No change';
    if (item.kind === 'known-form') return draft.remove ? `${beastName(draft.remove)} → ${beastName(draft.add)}` : 'No change';
    return sameDraft(item, draft) ? `No change (${describe(item, item.current)})` : `${describe(item, item.current)} → ${describe(item, draft)}`;
  };
  const weaponOptions = (item) => {
    const entitlement = getWeaponMasteryEntitlements(form, weapons).find((entry) => entry.sourceId === item.sourceId);
    const keys = new Set([...(entitlement?.eligibleWeaponKeys || []), ...item.current]);
    return [...keys].map((key) => ({ key, name: weaponName(key), mastery: weapons?.[key]?.mastery })).sort((a, b) => a.name.localeCompare(b.name));
  };

  return (
    <Modal className="dnd-modal combat-decision-prompt" centered show onHide={close} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{restType === 'long' ? 'Long Rest' : 'Short Rest'}: {reviewing ? 'review your changes' : choice.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small mb-3">{reviewing ? 'Nothing is saved until you choose Save.' : `Step ${step + 1} of ${choices.length + 1}. Keep or change it, then continue.`}</p>
        {error && <Alert variant="danger">{error}</Alert>}
        {choice?.kind === 'weapon-mastery' && <>
          <p className="small">{`You can replace up to ${choice.replacements} of your ${choice.count} mastered weapons.`}</p>
          {drafts[choice.key].map((value, index) => (
            <Form.Group className="mb-2" controlId={`rest-${choice.sourceId}-mastery-${index}`} key={index}>
              <Form.Label>{`Mastery ${index + 1}`}</Form.Label>
              <Form.Select value={value} onChange={(event) => setDraft(choice.key, drafts[choice.key].map((entry, i) => (i === index ? event.target.value : entry)))}>
                <option value="">Select weapon</option>
                {weaponOptions(choice).map((weapon) => (
                  <option key={weapon.key} value={weapon.key} disabled={drafts[choice.key].includes(weapon.key) && weapon.key !== value}>
                    {weapon.name}{weapon.mastery ? ` (${weapon.mastery})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ))}
          {overLimit && <p className="text-danger small" role="alert">{`Replace at most ${choice.replacements} weapon${choice.replacements === 1 ? '' : 's'}.`}</p>}
        </>}
        {choice?.kind === 'prepared-spell' && <>
          <p className="small">You can replace one prepared spell with another spell from your list that you have spell slots for.</p>
          <Form.Group className="mb-2" controlId={`rest-${choice.classId}-spell-remove`}>
            <Form.Label>Spell to replace</Form.Label>
            <Form.Select value={drafts[choice.key].remove} onChange={(event) => setDraft(choice.key, { remove: event.target.value, add: event.target.value ? drafts[choice.key].add : '' })}>
              <option value="">Keep all prepared spells</option>
              {choice.prepared.map((spell) => <option key={spell.spellId} value={spell.spellId}>{`${spell.name} (level ${spell.level})`}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId={`rest-${choice.classId}-spell-add`}>
            <Form.Label>New spell</Form.Label>
            <Form.Select value={drafts[choice.key].add} disabled={!drafts[choice.key].remove} onChange={(event) => setDraft(choice.key, { ...drafts[choice.key], add: event.target.value })}>
              <option value="">Choose a spell</option>
              {choice.options.map((spell) => <option key={spell.spellId} value={spell.spellId}>{`${spell.name} (level ${spell.level})`}</option>)}
            </Form.Select>
          </Form.Group>
        </>}
        {choice?.kind === 'druid-land' && <Form.Group controlId="rest-druid-land">
          <Form.Label>Land</Form.Label>
          <Form.Select value={drafts[choice.key]} onChange={(event) => setDraft(choice.key, event.target.value)}>
            {!choice.current && <option value="">Decide later</option>}
            {Object.values(LANDS).map((land) => <option key={land.id} value={land.id}>{land.name}</option>)}
          </Form.Select>
          <p className="small mt-2">Your Circle Spells{hasLandFeature(form, 'naturesWard') ? ' and Nature’s Ward' : ''} follow the land you choose.</p>
        </Form.Group>}
        {choice?.kind === 'known-form' && <>
          <p className="small">You can replace one of your known Beast forms with another eligible Beast.</p>
          <Form.Group className="mb-2" controlId="rest-known-form-remove">
            <Form.Label>Form to replace</Form.Label>
            <Form.Select value={drafts[choice.key].remove} onChange={(event) => setDraft(choice.key, { remove: event.target.value, add: event.target.value ? drafts[choice.key].add : '' })}>
              <option value="">Keep all forms</option>
              {choice.known.map((index) => <option key={index} value={index}>{beastName(index)}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="rest-known-form-add">
            <Form.Label>New form</Form.Label>
            <Form.Select value={drafts[choice.key].add} disabled={!drafts[choice.key].remove || !beasts} onChange={(event) => setDraft(choice.key, { ...drafts[choice.key], add: event.target.value })}>
              <option value="">{beasts ? 'Choose a Beast' : 'Loading Beasts…'}</option>
              {(beasts || []).filter((beast) => !choice.known.includes(beast.index)).map((beast) => <option key={beast.index} value={beast.index}>{beast.name}</option>)}
            </Form.Select>
          </Form.Group>
        </>}
        {choice?.kind === 'hunters-prey' && <Form.Group controlId="rest-hunters-prey">
          <Form.Label>Hunter's Prey</Form.Label>
          <Form.Select value={drafts[choice.key]} onChange={(event) => setDraft(choice.key, event.target.value)}>
            {HUNTERS_PREY_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
          </Form.Select>
          <p className="small mt-2">{HUNTERS_PREY_OPTIONS.find((option) => option.id === drafts[choice.key])?.summary}</p>
        </Form.Group>}
        {choice?.kind === 'defensive-tactics' && <Form.Group controlId="rest-defensive-tactics">
          <Form.Label>Defensive Tactics</Form.Label>
          <Form.Select value={drafts[choice.key]} onChange={(event) => setDraft(choice.key, event.target.value)}>
            {DEFENSIVE_TACTICS_OPTIONS.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
          </Form.Select>
          <p className="small mt-2">{DEFENSIVE_TACTICS_OPTIONS.find((option) => option.id === drafts[choice.key])?.summary}</p>
        </Form.Group>}
        {reviewing && <dl className="mb-0">
          {choices.map((item) => (
            <React.Fragment key={item.key}>
              <dt>{item.label}</dt>
              <dd>{reviewLine(item)}</dd>
            </React.Fragment>
          ))}
        </dl>}
      </Modal.Body>
      <Modal.Footer>
        {step > 0 && <Button variant="secondary" disabled={saving} onClick={() => go(step - 1)}>Back</Button>}
        {reviewing
          ? <Button disabled={saving} onClick={save}>{saving ? 'Saving…' : choices.some((item) => !sameDraft(item, drafts[item.key])) ? 'Save' : 'Done'}</Button>
          : <Button disabled={overLimit || duplicate || incomplete} onClick={() => go(step + 1)}>Continue</Button>}
      </Modal.Footer>
    </Modal>
  );
}
